import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import apiClient from "@/api/client";
import { canManageContractors } from "@/features/contractors/utils";

export type QualificationStatus = "full" | "conditional";

/** Pozycja wewnątrz kategorii — konkretna robota. */
export interface CooperationScope {
  id: string;
  category: string;
  category_name: string;
  name: string;
  description: string;
  is_active: boolean;
  sort_order: number;
}

/** Kategoria — jednostka, którą nadajemy podwykonawcy. */
export interface ScopeCategory {
  id: string;
  name: string;
  description: string;
  is_active: boolean;
  sort_order: number;
  scopes: CooperationScope[];
  contractor_count: number;
}

export interface CategoryQualification {
  id: string;
  category: string;
  category_name: string;
  status: QualificationStatus;
  status_label: string;
  /** Czy kwalifikacja obejmuje całą kategorię, także pozycje dodane w przyszłości. */
  all_positions: boolean;
  /** Wypełnione tylko przy kwalifikacji częściowej. */
  positions: { id: string; name: string }[];
  note: string;
  granted_at: string;
  granted_by_name: string | null;
}

export interface CountyOption {
  /** Kod TERYT powiatu (WWPP), np. „2204”. */
  teryt: string;
  name: string;
}

/** Województwo ze słownika — razem z kompletem powiatów do doprecyzowania zasięgu. */
export interface VoivodeshipOption {
  value: string;
  label: string;
  teryt: string;
  counties: CountyOption[];
}

export interface AreaCoverage {
  id: string;
  voivodeship: string;
  voivodeship_label: string;
  /** Kody TERYT powiatów zawężających województwo; pusta lista = całe województwo. */
  counties: string[];
  counties_detail: CountyOption[];
  /** Ile powiatów ma województwo — do „3 z 20”. */
  county_total: number;
  granted_at: string;
}

export interface ContractorQualification {
  contractor: string;
  contractor_name: string;
  categories: CategoryQualification[];
  voivodeships: AreaCoverage[];
}

export interface QualificationPayload {
  /** Brak klucza = kategorie bez zmian. Bez prawa dyrektora technicznego wysłanie go = 403. */
  categories?: {
    category: string;
    status: QualificationStatus;
    all_positions: boolean;
    positions: string[];
    note: string;
  }[];
  voivodeships: string[];
  /** Zawężenie do powiatów: {kod województwa: [kody TERYT]}; brak wpisu = całe województwo. */
  counties?: Record<string, string[]>;
}

export function useScopeCategories(includeInactive = false) {
  return useQuery({
    queryKey: ["rfq", "categories", includeInactive],
    queryFn: () =>
      apiClient
        .get<{ data: ScopeCategory[] }>("/rfq/categories/", {
          params: includeInactive ? { include_inactive: "1" } : undefined,
        })
        .then((r) => r.data.data),
  });
}

export function useVoivodeships() {
  return useQuery({
    queryKey: ["rfq", "voivodeships"],
    // Stała lista 16 województw (z 380 powiatami) — pobieramy raz na sesję.
    staleTime: Infinity,
    queryFn: () =>
      apiClient
        .get<{ data: VoivodeshipOption[] }>("/rfq/voivodeships/")
        .then((r) => r.data.data),
  });
}

function invalidateDictionary(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: ["rfq", "categories"] });
}

export function useSaveCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: Partial<ScopeCategory> & { id?: string }) =>
      id
        ? apiClient.patch(`/rfq/categories/${id}/`, body)
        : apiClient.post("/rfq/categories/", body),
    onSuccess: () => invalidateDictionary(qc),
  });
}

export function useDeleteCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiClient.delete(`/rfq/categories/${id}/`),
    onSuccess: () => invalidateDictionary(qc),
  });
}

export function useSaveScope() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: Partial<CooperationScope> & { id?: string }) =>
      id
        ? apiClient.patch(`/rfq/cooperation-scopes/${id}/`, body)
        : apiClient.post("/rfq/cooperation-scopes/", body),
    onSuccess: () => invalidateDictionary(qc),
  });
}

export function useDeleteScope() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiClient.delete(`/rfq/cooperation-scopes/${id}/`),
    onSuccess: () => invalidateDictionary(qc),
  });
}

export interface CategoryHolder {
  contractor_id: string;
  contractor_name: string;
  status: QualificationStatus;
  status_label: string;
  all_positions: boolean;
  positions: { id: string; name: string }[];
  granted_at: string;
  granted_by_name: string | null;
}

/** Kto ma daną kategorię i w jakim zakresie — rozwinięcie licznika przy kategorii. */
export function useCategoryHolders(categoryId: string | null) {
  return useQuery({
    queryKey: ["rfq", "category-holders", categoryId],
    enabled: !!categoryId,
    queryFn: () =>
      apiClient
        .get<{ data: CategoryHolder[] }>(`/rfq/categories/${categoryId}/contractors/`)
        .then((r) => r.data.data),
  });
}

export function useContractorQualification(contractorId: string | undefined) {
  return useQuery({
    queryKey: ["rfq", "qualification", contractorId],
    enabled: !!contractorId,
    queryFn: () =>
      apiClient
        .get<{ data: ContractorQualification }>(`/rfq/contractors/${contractorId}/qualification/`)
        .then((r) => r.data.data),
  });
}

export function useSaveContractorQualification(contractorId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: QualificationPayload) =>
      apiClient.put(`/rfq/contractors/${contractorId}/qualification/`, payload),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ["rfq", "qualification", contractorId] }),
  });
}

/** Czy zalogowany może rozstrzygać wnioski o rozszerzenie i prowadzić domyślne oświadczenia. */
export function canManageRfqScopes(user: {
  is_ceo?: boolean;
  role?: string;
  is_technical_director?: boolean;
} | null | undefined) {
  return !!user?.is_ceo || (user?.role === "director" && !!user?.is_technical_director);
}

/**
 * Czy zalogowany może redagować słownik „Kategorie i zakresy" (kategorie i pozycje).
 * Ten sam krąg, co nadawanie kategorii firmom: CEO, dyr. techniczny i uprawnienie
 * „Podwykonawcy i umowy ramowe" — jak backendowa bramka CanManageContractors.
 */
export function canEditScopeDictionary(
  user: Parameters<typeof canManageContractors>[0],
) {
  return canManageContractors(user);
}
